import { GameState } from "../game/state";
import { Palette } from "../data/palette";
import { maxIntegrity } from "../game/stats";
import { regionName } from "../data/regions";
import { getPrestige, type ScoreEntry } from "./leaderboard";
import type { Settings } from "./settings";
import { stageInfo } from "../data/stages";
import { TAU } from "../core/math";
import { META_UPGRADES, metaLevel, metaNextCost, type MetaState } from "./meta";
import { SPRITES, drawSprite } from "../render/sprites";
import { drawIcon } from "./icons";

// Screen-space HUD + menu overlays. All player info lives in a bar across the
// BOTTOM of the window; the board fills everything above it.
export const HUD_BAR = 72;

export function drawHud(ctx: CanvasRenderingContext2D, s: GameState, w: number, h: number): void {
  const top = h - HUD_BAR;

  // ── left: bars ────────────────────────────────────────────────────────────
  // Bar width shrinks on narrow (phone) screens so the three HUD columns fit.
  const bw = Math.min(240, Math.max(120, w * 0.34));
  const maxI = maxIntegrity(s.stats);
  bar(ctx, 16, top + 12, bw, 16, s.player.integrity / maxI, Palette.integrity, "INTEGRITY");
  ctx.fillStyle = "#e4e4e4";
  ctx.font = '11px "Chakra Petch", "Courier New", monospace';
  ctx.textAlign = "right";
  ctx.fillText(`${Math.ceil(s.player.integrity)}/${maxI}`, 16 + bw - 4, top + 24);

  bar(ctx, 16, top + 34, bw, 9, s.bandwidth / s.nextLevelAt, Palette.bandwidth, "");
  bar(ctx, 16, top + 48, bw, 7, s.signal / s.signalMax, Palette.signal, "");
  ctx.textAlign = "left";
  ctx.fillStyle = Palette.bandwidth;
  ctx.font = 'bold 13px "Chakra Petch", "Courier New", monospace';
  ctx.fillText(`LV ${s.level}`, 16 + bw + 12, top + 42);
  ctx.fillStyle = Palette.signal;
  ctx.font = '11px "Chakra Petch", "Courier New", monospace';
  ctx.fillText(s.signal >= s.signalMax ? "SURGE READY [R]" : "SIGNAL", 16 + bw + 12, top + 54);

  // ── center: zone survival countdown (or BOSS while one is active) ──────────
  const bossActive = !!(s.bossEnemy && s.bossEnemy.alive);
  ctx.textAlign = "center";
  if (bossActive) {
    ctx.fillStyle = Palette.danger;
    ctx.font = 'bold 26px "Chakra Petch", "Courier New", monospace';
    ctx.fillText("BOSS", w / 2, top + 30);
  } else {
    ctx.fillStyle = s.zoneTimer < 10 ? Palette.danger : "#e4e4e4";
    ctx.font = 'bold 32px "Chakra Petch", "Courier New", monospace';
    ctx.fillText(formatTime(Math.max(0, s.zoneTimer)), w / 2, top + 32);
  }
  ctx.font = '13px "Chakra Petch", "Courier New", monospace';
  ctx.fillStyle = "#80a0ff";
  ctx.fillText(`ZONE ${s.zone} — ${regionName(s.zone).toUpperCase()}`, w / 2, top + 52);
  ctx.fillStyle = "#949494";
  ctx.font = '11px "Chakra Petch", "Courier New", monospace';
  ctx.fillText("DEFEND THE NODES", w / 2, top + 66);

  // ── right: objective + stats ──────────────────────────────────────────────
  const online = s.nodes.filter((n) => n.online).length;
  const threatened = online < s.nodes.length || s.nodes.some((n) => n.underAttack > 0);
  const nodeColor = online === 0 ? Palette.danger : threatened ? Palette.worm : Palette.node;
  ctx.textAlign = "right";
  ctx.fillStyle = nodeColor;
  ctx.font = 'bold 18px "Chakra Petch", "Courier New", monospace';
  ctx.shadowBlur = threatened ? 8 : 0;
  ctx.shadowColor = nodeColor;
  ctx.fillText(`NODES ${online}/${s.nodes.length}`, w - 16, top + 26);
  ctx.shadowBlur = 0;
  line(ctx, w - 16, top + 34, `FRAGMENTS ${s.fragments}`, Palette.fragment);
  line(ctx, w - 16, top + 52, `KILLS ${s.kills}`, "#e4e4e4");
}

/** Top-left list of upgrades taken this run, with stack counts (a buff bar). */
export function drawUpgrades(ctx: CanvasRenderingContext2D, s: GameState): void {
  const ups = s.upgradeStacks;
  if (ups.length === 0) return;
  const x = 12;
  const y0 = 14;
  const rh = 16;

  ctx.save();
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.fillRect(8, 8, 210, ups.length * rh + 8);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = '11px "Chakra Petch", "Courier New", monospace';
  ups.forEach((u, i) => {
    const y = y0 + i * rh;
    drawIcon(ctx, u.icon, x + 6, y - 4, 13, u.color);
    ctx.fillStyle = "#bdbdbd";
    const label = u.count > 1 ? `${u.name.toUpperCase()} x${u.count}` : u.name.toUpperCase();
    ctx.fillText(label, x + 18, y);
  });
  ctx.restore();
}

function bar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  frac: number,
  color: string,
  label: string,
): void {
  frac = Math.max(0, Math.min(1, frac));
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = color;
  ctx.shadowBlur = 8;
  ctx.shadowColor = color;
  ctx.fillRect(x, y, w * frac, h);
  ctx.shadowBlur = 0;
  ctx.strokeStyle = "rgba(255,255,255,0.20)";
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, w, h);
  if (label) {
    ctx.fillStyle = "#080808";
    ctx.font = `bold ${Math.min(h - 4, 11)}px "Chakra Petch", "Courier New", monospace`;
    ctx.textAlign = "left";
    ctx.fillText(label, x + 6, y + h - 4);
  }
}

function line(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, color: string): void {
  ctx.fillStyle = color;
  ctx.font = 'bold 13px "Chakra Petch", "Courier New", monospace';
  ctx.textAlign = "right";
  ctx.fillText(text, x, y + 12);
}

function formatTime(t: number): string {
  const m = Math.floor(t / 60);
  const sec = Math.floor(t % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

/** Top-center boss name + HP bar while a boss is active. */
export function drawBossBar(ctx: CanvasRenderingContext2D, s: GameState, w: number): void {
  const b = s.bossEnemy;
  if (!b || !b.alive || !b.bossState) return;
  const bw = Math.min(560, w * 0.5);
  const x = (w - bw) / 2;
  const y = 18;
  const frac = Math.max(0, b.hp / b.maxHp);
  ctx.save();
  ctx.textAlign = "center";
  ctx.fillStyle = b.def.color;
  ctx.shadowBlur = 10;
  ctx.shadowColor = b.def.color;
  ctx.font = '700 15px "Orbitron", "Chakra Petch", sans-serif';
  ctx.fillText(b.bossState.name.toUpperCase(), w / 2, y);
  ctx.shadowBlur = 0;
  ctx.fillStyle = "rgba(255,255,255,0.10)";
  ctx.fillRect(x, y + 8, bw, 12);
  ctx.fillStyle = b.def.color;
  ctx.fillRect(x, y + 8, bw * frac, 12);
  ctx.strokeStyle = "rgba(255,255,255,0.25)";
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y + 8, bw, 12);
  ctx.restore();
}

// ── overlays ─────────────────────────────────────────────────────────────────

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Tappable/clickable regions on the title menu (kept in sync with drawTitle). */
export function titleHitRects(w: number, h: number): {
  shake: Rect;
  flash: Rect;
  start: Rect;
  lab: Rect;
} {
  const cx = w / 2;
  const ty = h / 2 - 8; // accessibility toggles
  const sy = h / 2 + 48; // START
  const ly = h / 2 + 100; // UPGRADE LAB
  return {
    shake: { x: cx - 166, y: ty - 16, w: 156, h: 30 },
    flash: { x: cx + 10, y: ty - 16, w: 156, h: 30 },
    start: { x: cx - 100, y: sy - 22, w: 200, h: 44 },
    lab: { x: cx - 110, y: ly - 16, w: 220, h: 32 },
  };
}

export function drawTitle(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  t: number,
  settings: Settings,
  meta: MetaState,
): void {
  dim(ctx, w, h, 0.82);
  ctx.textAlign = "center";

  // PCKT character hovering above the title.
  const charY = h / 2 - 250 + Math.sin(t * 2) * 4;
  ctx.save();
  ctx.globalAlpha = 0.45;
  ctx.shadowBlur = 22;
  ctx.shadowColor = Palette.pckt;
  ctx.fillStyle = Palette.pckt;
  ctx.beginPath();
  ctx.arc(w / 2, charY, 26, 0, TAU);
  ctx.fill();
  ctx.restore();

  // Energy field flowing around the title character.
  ctx.save();
  ctx.translate(w / 2, charY);
  const fr = 52;
  ctx.shadowBlur = 12;
  ctx.shadowColor = Palette.bandwidth;
  ctx.strokeStyle = Palette.bandwidth;
  ctx.lineWidth = 2.5;
  for (let i = 0; i < 2; i++) {
    const a0 = t * (i === 0 ? 2.2 : -1.6) + i * Math.PI;
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.arc(0, 0, fr + i * 8, a0, a0 + Math.PI * 0.6);
    ctx.stroke();
  }
  for (let i = 0; i < 6; i++) {
    const a = t * 1.8 + (i / 6) * TAU;
    const rr = fr + Math.sin(t * 4 + i) * 7;
    ctx.globalAlpha = 0.55 + 0.35 * Math.sin(t * 5 + i);
    ctx.fillStyle = i % 2 === 0 ? Palette.pckt : Palette.node;
    ctx.beginPath();
    ctx.arc(Math.cos(a) * rr, Math.sin(a) * rr, 3, 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  drawSprite(ctx, SPRITES.pckt, w / 2, charY, 7);

  const glow = 16 + Math.sin(t * 3) * 8;
  ctx.shadowBlur = glow;
  ctx.shadowColor = Palette.signal;
  ctx.fillStyle = Palette.pckt;
  ctx.font = '900 56px "Orbitron", "Chakra Petch", sans-serif';
  ctx.fillText("pckt", w / 2, h / 2 - 168); // only "pckt" is lowercase, as a brand mark
  ctx.fillStyle = Palette.signal;
  ctx.font = '700 32px "Orbitron", "Chakra Petch", sans-serif';
  ctx.fillText("Glitch Rush", w / 2, h / 2 - 126);
  ctx.shadowBlur = 0;

  // Hub stats line: banked fragments + best zone + prestige.
  ctx.fillStyle = Palette.fragment;
  ctx.font = '13px "Chakra Petch", "Courier New", monospace';
  const best = meta.bestZone > 0 ? `   •   BEST ZONE ${meta.bestZone}` : "";
  ctx.fillText(`◆ ${meta.fragments} FRAGMENTS BANKED${best}`, w / 2, h / 2 - 98);
  const prestige = getPrestige();
  if (prestige > 0) {
    ctx.fillStyle = Palette.signal;
    ctx.font = 'bold 14px "Chakra Petch", "Courier New", monospace';
    ctx.fillText(`◈ ZERO DAY DEFEATED ×${prestige}`, w / 2, h / 2 - 78);
  }

  const r = titleHitRects(w, h);

  // Accessibility toggles
  chip(ctx, r.shake, `SHAKE ${settings.screenShake ? "ON" : "OFF"}`, settings.screenShake);
  chip(ctx, r.flash, `REDUCE FLASH ${settings.reduceFlashing ? "ON" : "OFF"}`, settings.reduceFlashing);

  // START button
  ctx.strokeStyle = "#e4e4e4";
  ctx.lineWidth = 2;
  ctx.strokeRect(r.start.x, r.start.y, r.start.w, r.start.h);
  ctx.fillStyle = Math.floor(t * 2) % 2 === 0 ? "#ffffff" : "#bdbdbd";
  ctx.font = 'bold 22px "Chakra Petch", "Courier New", monospace';
  ctx.textBaseline = "middle";
  ctx.fillText("START", w / 2, r.start.y + r.start.h / 2 + 1);
  ctx.textBaseline = "alphabetic";

  // UPGRADE LAB button
  ctx.strokeStyle = Palette.fragment;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(r.lab.x, r.lab.y, r.lab.w, r.lab.h);
  ctx.fillStyle = Palette.fragment;
  ctx.font = 'bold 15px "Chakra Petch", "Courier New", monospace';
  ctx.textBaseline = "middle";
  ctx.fillText("◆ UPGRADE LAB", w / 2, r.lab.y + r.lab.h / 2 + 1);
  ctx.textBaseline = "alphabetic";

  // hint (below the lab button so it never overlaps it)
  const touch = isTouch();
  ctx.fillStyle = "#9fb3d9";
  ctx.font = `${w < 560 ? 10 : 12}px "Chakra Petch", "Courier New", monospace`;
  ctx.fillText(
    touch ? "TAP START TO PLAY • TAP A TOGGLE TO CHANGE IT" : "ENTER START • K SHAKE • F FLASH",
    w / 2,
    r.lab.y + r.lab.h + 26,
  );
  const hint = touch
    ? "LEFT: MOVE   •   RIGHT: FIRE   •   SURGE BUTTON: PURGE"
    : "MOVE: WASD/ARROWS   •   TAP SPACE: FIRE   •   R: SURGE   •   WEAPONS AUTO-FIRE";
  ctx.fillText(hint, w / 2, h - 46);

  // copyright
  ctx.fillStyle = "#6a6a72";
  ctx.font = '11px "Chakra Petch", "Courier New", monospace';
  ctx.fillText("© d5rptr 2026", w / 2, h - 22);
}

function chip(ctx: CanvasRenderingContext2D, r: Rect, label: string, on: boolean): void {
  ctx.save();
  ctx.strokeStyle = on ? Palette.integrity : "#666";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(r.x, r.y, r.w, r.h);
  ctx.fillStyle = on ? Palette.integrity : "#9aa";
  ctx.font = 'bold 12px "Chakra Petch", "Courier New", monospace';
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  ctx.textBaseline = "alphabetic";
  ctx.restore();
}

export interface CardRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Layout for the choose-one upgrade cards. Wide screens lay them out in a row;
 * narrow (phone) screens stack them vertically so each is a comfortable tap
 * target. Shared by the renderer and touch hit-testing so they never drift.
 */
export function levelUpCardRects(n: number, w: number, h: number): { rects: CardRect[]; vertical: boolean } {
  const vertical = w < 760;
  const rects: CardRect[] = [];
  if (vertical) {
    const cw = Math.min(w - 32, 460);
    const gap = 12;
    const ch = Math.min(112, Math.max(72, (h * 0.6) / Math.max(1, n) - gap));
    const totalH = n * ch + (n - 1) * gap;
    const x = (w - cw) / 2;
    let y = h / 2 - totalH / 2 + 28;
    for (let i = 0; i < n; i++) {
      rects.push({ x, y, w: cw, h: ch });
      y += ch + gap;
    }
  } else {
    const cw = 240;
    const gap = 24;
    const ch = 220;
    const totalW = n * cw + (n - 1) * gap;
    const startX = (w - totalW) / 2;
    const cy = h / 2 - 80;
    for (let i = 0; i < n; i++) rects.push({ x: startX + i * (cw + gap), y: cy, w: cw, h: ch });
  }
  return { rects, vertical };
}

export function drawLevelUp(ctx: CanvasRenderingContext2D, s: GameState, w: number, h: number, t: number): void {
  dim(ctx, w, h, 0.82);
  const { rects, vertical } = levelUpCardRects(s.pendingChoices.length, w, h);
  const headerY = vertical ? rects[0].y - 52 : h / 2 - 150;
  ctx.textAlign = "center";
  ctx.fillStyle = Palette.bandwidth;
  ctx.shadowBlur = 12;
  ctx.shadowColor = Palette.bandwidth;
  ctx.font = `bold ${vertical ? 26 : 34}px "Chakra Petch", "Courier New", monospace`;
  ctx.fillText("UPGRADE ACQUIRED", w / 2, headerY);
  ctx.shadowBlur = 0;
  ctx.fillStyle = "#9fd";
  ctx.font = '13px "Chakra Petch", "Courier New", monospace';
  ctx.fillText(vertical ? "TAP A CARD TO CHOOSE" : "CHOOSE ONE  —  1/2/3 or ←/→ + ENTER", w / 2, headerY + 26);

  s.pendingChoices.forEach((u, i) => {
    const r = rects[i];
    const selected = i === s.selIndex;
    ctx.save();
    if (selected) {
      ctx.shadowBlur = 20 + Math.sin(t * 8) * 6;
      ctx.shadowColor = u.color;
    }
    ctx.fillStyle = "rgba(5,5,20,0.92)";
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = u.color;
    ctx.lineWidth = selected ? 3 : 1.5;
    ctx.strokeRect(r.x, r.y, r.w, r.h);
    ctx.shadowBlur = 0;

    if (vertical) {
      // Compact card: number + name on top, description below, left-aligned.
      ctx.textAlign = "left";
      ctx.fillStyle = u.color;
      ctx.font = 'bold 17px "Chakra Petch", "Courier New", monospace';
      ctx.fillText(`${i + 1}. ${u.name.toUpperCase()}`, r.x + 14, r.y + 26);
      ctx.fillStyle = "#cfe";
      ctx.font = '13px "Chakra Petch", "Courier New", monospace';
      wrap(ctx, u.description, r.x + 14, r.y + 48, r.w - 56, 18);
      drawIcon(ctx, u.icon, r.x + r.w - 30, r.y + r.h / 2, 28, u.color);
    } else {
      drawIcon(ctx, u.icon, r.x + r.w / 2, r.y + 158, 44, u.color);
      ctx.textAlign = "center";
      ctx.fillStyle = u.color;
      ctx.font = 'bold 18px "Chakra Petch", "Courier New", monospace';
      wrap(ctx, u.name.toUpperCase(), r.x + r.w / 2, r.y + 44, r.w - 24, 22);
      ctx.fillStyle = "#cfe";
      ctx.font = '14px "Chakra Petch", "Courier New", monospace';
      wrap(ctx, u.description, r.x + r.w / 2, r.y + 110, r.w - 32, 20);
      ctx.fillStyle = selected ? "#ffffff" : "#9fb3d9";
      ctx.font = 'bold 14px "Chakra Petch", "Courier New", monospace';
      ctx.fillText(`[ ${i + 1} ]`, r.x + r.w / 2, r.y + r.h - 22);
    }
    ctx.restore();
  });
}

export function drawPause(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  dim(ctx, w, h, 0.74);
  ctx.textAlign = "center";
  ctx.fillStyle = "#ffffff";
  ctx.font = 'bold 40px "Chakra Petch", "Courier New", monospace';
  ctx.fillText("PAUSED", w / 2, h / 2 - 10);
  ctx.fillStyle = "#9fd";
  ctx.font = '14px "Chakra Petch", "Courier New", monospace';
  ctx.fillText(isTouch() ? "TAP TO RESUME" : "ESC / P / B TO RESUME", w / 2, h / 2 + 26);
}

export function drawGameOver(
  ctx: CanvasRenderingContext2D,
  s: GameState,
  w: number,
  h: number,
  t: number,
  entries: ScoreEntry[] = [],
): void {
  dim(ctx, w, h, 0.85);
  ctx.textAlign = "center";
  ctx.shadowBlur = 16;
  ctx.shadowColor = Palette.danger;
  ctx.fillStyle = Palette.danger;
  ctx.font = 'bold 48px "Chakra Petch", "Courier New", monospace';
  ctx.fillText("NETWORK COLLAPSED", w / 2, h / 2 - 140);
  ctx.shadowBlur = 0;

  ctx.fillStyle = "#fff";
  ctx.font = '18px "Chakra Petch", "Courier New", monospace';
  ctx.fillText(`SURVIVED ${formatTime(s.time)}  •  ZONE ${s.zone}`, w / 2, h / 2 - 100);
  ctx.fillStyle = "#9fd";
  ctx.font = '15px "Chakra Petch", "Courier New", monospace';
  ctx.fillText(`LEVEL ${s.level}   •   ${s.kills} PURGED   •   ${s.fragments} FRAGMENTS`, w / 2, h / 2 - 76);

  // Leaderboard (top entries from the Worker API).
  ctx.fillStyle = Palette.fragment;
  ctx.font = 'bold 16px "Chakra Petch", "Courier New", monospace';
  ctx.fillText("— GLOBAL LEADERBOARD (BY ZONE) —", w / 2, h / 2 - 40);
  ctx.font = '14px "Chakra Petch", "Courier New", monospace';
  if (entries.length === 0) {
    ctx.fillStyle = "#8aa";
    ctx.fillText("submitting…", w / 2, h / 2 - 16);
  } else {
    entries.slice(0, 8).forEach((e, i) => {
      ctx.fillStyle = i === 0 ? Palette.fragment : "#bdbdbd";
      const z = `Z${e.zone ?? 1}`.padEnd(4, " ");
      const row = `${(i + 1).toString().padStart(2, " ")}  ${e.name.padEnd(14, " ").slice(0, 14)}  ${z}  ${formatTime(e.time)}`;
      ctx.fillText(row, w / 2, h / 2 - 16 + i * 20);
    });
  }

  if (Math.floor(t * 2) % 2 === 0) {
    ctx.fillStyle = "#ffffff";
    ctx.font = 'bold 18px "Chakra Petch", "Courier New", monospace';
    ctx.fillText(isTouch() ? "TAP TO REDEPLOY" : "PRESS ENTER / A TO REDEPLOY", w / 2, h / 2 + 150);
  }
}

/** Story interstitial shown when entering a new stage. */
export function drawStageIntro(ctx: CanvasRenderingContext2D, index: number, w: number, h: number, t: number): void {
  const info = stageInfo(index);
  if (!info) return;
  dim(ctx, w, h, 0.9);
  ctx.textAlign = "center";
  ctx.fillStyle = Palette.pckt;
  ctx.shadowBlur = 14;
  ctx.shadowColor = Palette.pckt;
  ctx.font = `900 ${w < 560 ? 20 : 26}px "Orbitron", "Chakra Petch", sans-serif`;
  ctx.fillText(info.name, w / 2, h / 2 - 92);
  ctx.shadowBlur = 0;
  ctx.fillStyle = "#cfe";
  ctx.font = `${w < 560 ? 14 : 17}px "Chakra Petch", "Courier New", monospace`;
  info.lines.forEach((ln, i) => wrap(ctx, ln, w / 2, h / 2 - 38 + i * 30, Math.min(w - 48, 640), 26));
  if (Math.floor(t * 2) % 2 === 0) {
    ctx.fillStyle = "#ffffff";
    ctx.font = 'bold 16px "Chakra Petch", "Courier New", monospace';
    ctx.fillText(isTouch() ? "TAP TO CONTINUE" : "PRESS ENTER TO CONTINUE", w / 2, h / 2 + 84);
  }
}

/** Canvas SURGE recharge dial (desktop) — bottom-right, above the HUD bar. */
export function drawSurgeDial(ctx: CanvasRenderingContext2D, s: GameState, w: number, h: number): void {
  const frac = Math.max(0, Math.min(1, s.signal / s.signalMax));
  const ready = frac >= 1;
  const r = 30;
  const cx = w - 46;
  const cy = h - HUD_BAR - 46;
  ctx.save();
  ctx.lineWidth = 5;
  ctx.strokeStyle = "rgba(207,135,232,0.22)";
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = Palette.signal;
  ctx.shadowColor = Palette.signal;
  ctx.shadowBlur = ready ? 16 : 8;
  ctx.beginPath();
  ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + frac * TAU);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = ready ? "#ffffff" : "#cf87e8";
  ctx.font = 'bold 11px "Chakra Petch", "Courier New", monospace';
  ctx.fillText("SURGE", cx, cy - 4);
  ctx.font = '9px "Chakra Petch", "Courier New", monospace';
  ctx.fillText(ready ? "[R]" : `${Math.round(frac * 100)}%`, cx, cy + 8);
  ctx.textBaseline = "alphabetic";
  ctx.restore();
}

/** Clickable rows + back button for the Upgrade Lab. */
export function labHitRects(w: number, h: number): { rows: Rect[]; back: Rect } {
  const n = META_UPGRADES.length;
  const rowW = Math.min(w - 40, 560);
  const rowH = 44;
  const x = (w - rowW) / 2;
  const top = h / 2 - (n * rowH) / 2 + 6;
  const rows = META_UPGRADES.map((_, i) => ({ x, y: top + i * rowH, w: rowW, h: rowH - 6 }));
  const back = { x: w / 2 - 80, y: top + n * rowH + 16, w: 160, h: 38 };
  return { rows, back };
}

export function drawLab(ctx: CanvasRenderingContext2D, meta: MetaState, w: number, h: number, sel: number): void {
  dim(ctx, w, h, 0.92);
  const { rows, back } = labHitRects(w, h);

  ctx.textAlign = "center";
  ctx.fillStyle = Palette.fragment;
  ctx.shadowBlur = 12;
  ctx.shadowColor = Palette.fragment;
  ctx.font = 'bold 28px "Chakra Petch", "Courier New", monospace';
  ctx.fillText("UPGRADE LAB", w / 2, rows[0].y - 52);
  ctx.shadowBlur = 0;
  ctx.fillStyle = "#cfe";
  ctx.font = '14px "Chakra Petch", "Courier New", monospace';
  ctx.fillText(`◆ ${meta.fragments} FRAGMENTS BANKED`, w / 2, rows[0].y - 26);

  META_UPGRADES.forEach((u, i) => {
    const r = rows[i];
    const lvl = metaLevel(meta, u.id);
    const cost = metaNextCost(meta, u);
    const maxed = cost === null;
    const affordable = !maxed && meta.fragments >= (cost as number);
    const selected = i === sel;
    ctx.strokeStyle = selected ? "#e4e4e4" : "#555";
    ctx.lineWidth = selected ? 2 : 1;
    ctx.strokeRect(r.x, r.y, r.w, r.h);

    const iconColor = maxed ? Palette.integrity : "#cfe";
    drawIcon(ctx, u.icon, r.x + 22, r.y + r.h / 2, 24, iconColor);
    ctx.textAlign = "left";
    ctx.fillStyle = "#e4e4e4";
    ctx.font = 'bold 14px "Chakra Petch", "Courier New", monospace';
    ctx.fillText(`${u.name}  Lv${lvl}/${u.maxLevel}`, r.x + 44, r.y + 17);
    ctx.fillStyle = "#9fb3d9";
    ctx.font = '12px "Chakra Petch", "Courier New", monospace';
    ctx.fillText(u.desc, r.x + 44, r.y + 32);

    ctx.textAlign = "right";
    ctx.fillStyle = maxed ? Palette.integrity : affordable ? Palette.fragment : "#a55";
    ctx.font = 'bold 15px "Chakra Petch", "Courier New", monospace';
    ctx.fillText(maxed ? "MAX" : `◆ ${cost}`, r.x + r.w - 12, r.y + 24);
  });

  ctx.strokeStyle = "#9fb3d9";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(back.x, back.y, back.w, back.h);
  ctx.fillStyle = "#cfe";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = 'bold 16px "Chakra Petch", "Courier New", monospace';
  ctx.fillText("◀ BACK", w / 2, back.y + back.h / 2 + 1);
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = "#9fb3d9";
  ctx.font = '12px "Chakra Petch", "Courier New", monospace';
  ctx.fillText(isTouch() ? "TAP AN UPGRADE TO BUY" : "↑/↓ SELECT • ENTER BUY • ESC BACK", w / 2, back.y + back.h + 24);
}

// helpers
function isTouch(): boolean {
  return typeof document !== "undefined" && document.body.classList.contains("touch");
}

function dim(ctx: CanvasRenderingContext2D, w: number, h: number, a: number): void {
  ctx.fillStyle = `rgba(2,0,10,${a})`;
  ctx.fillRect(0, 0, w, h);
}

function wrap(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, lh: number): void {
  const words = text.split(" ");
  let lineStr = "";
  let yy = y;
  for (const word of words) {
    const test = lineStr ? lineStr + " " + word : word;
    if (ctx.measureText(test).width > maxW && lineStr) {
      ctx.fillText(lineStr, x, yy);
      lineStr = word;
      yy += lh;
    } else {
      lineStr = test;
    }
  }
  if (lineStr) ctx.fillText(lineStr, x, yy);
}
